import { useEffect, useState } from "react";
import { Link } from "wouter";
import NextPayoutCountdown from "../components/NextPayoutCountdown";
import SpendStatusBanner from "../components/SpendStatusBanner";
import {
  INDEX_SYMBOL,
  explainSpendSkip,
  fmtSol,
  fmtTime,
  loadCycleHistory,
  shortMint,
  spendWaitingOnFees,
} from "../lib/data";

function solscanTx(sig) {
  return sig ? `https://solscan.io/tx/${sig}` : null;
}

export default function CyclesPage({ data }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const spend = data?.spend;
  const metrics = data?.metrics;
  const schedule = data?.schedule || {};
  const claimable = metrics?.claimableQuote?.amountTokens;
  const waiting = spendWaitingOnFees(spend);
  const lastSkip = spend?.lastAttempt?.skipped || spend?.skipped;

  useEffect(() => {
    loadCycleHistory()
      .then((h) => setRows(h.versions))
      .catch((e) => setErr(e.message));
  }, []);

  return (
    <section className="app-section" style={{ paddingTop: "2.5rem" }}>
      <div className="shell">
        <p className="section-kicker">
          <i className="brand-diamond" aria-hidden /> Cycles
        </p>
        <h2 className="section-title">Distributions</h2>
        <p className="section-lead left">
          Each spend cycle turns {`$${INDEX_SYMBOL}`} fees into basket buys and
          holder airdrops. Windows can skip when liquid fees are under the
          minimum — that is expected, not an outage.
        </p>

        <NextPayoutCountdown schedule={schedule} spend={spend} />
        <SpendStatusBanner data={data} />

        <div className="portfolio-summary" style={{ marginTop: "1.25rem" }}>
          <div className="portfolio-stat">
            <div className="label">Claimable fees</div>
            <strong>{claimable != null ? `${fmtSol(claimable)} SOL` : "—"}</strong>
            <p>{waiting ? "Below spend gate" : "Ready for the next cycle"}</p>
          </div>
          <div className="portfolio-stat">
            <div className="label">Cycles run</div>
            <strong>{spend?.cycleCount ?? 0}</strong>
            <p>Archived spend runs</p>
          </div>
          <div className="portfolio-stat">
            <div className="label">Last cycle</div>
            <strong style={{ fontSize: "1.05rem" }}>
              {spend?.at ? fmtTime(spend.at) : "—"}
            </strong>
            <p>
              {lastSkip
                ? explainSpendSkip(lastSkip)
                : spend?.cycleSol != null
                  ? `${fmtSol(spend.cycleSol)} SOL spent`
                  : "Waiting on first spend"}
            </p>
          </div>
          <div className="portfolio-stat">
            <div className="label">Last holders</div>
            <strong>{spend?.holderCount ?? "—"}</strong>
            <p>Paid in the latest run</p>
          </div>
          <div className="portfolio-stat">
            <div className="label">All-time buys</div>
            <strong>
              {data?.payoutTotals?.totalSolAll != null
                ? `${fmtSol(data.payoutTotals.totalSolAll)} SOL`
                : "—"}
            </strong>
            <p>Across every basket name</p>
          </div>
        </div>

        {err && <p className="error">{err}</p>}
        {!rows && !err && <p className="loading">Loading cycles…</p>}

        {rows && rows.length === 0 && (
          <div className="panel">
            <h3>No cycles yet</h3>
            <p>
              When the worker clears the fee gate, each buy → airdrop archives
              here with signatures.
            </p>
            <Link href="/docs" className="btn btn-ghost">
              How cycles work
            </Link>
          </div>
        )}

        {rows && rows.length > 0 && (
          <div className="history-grid">
            {rows.map((c) => {
              const skipLabel = c.skipped ? explainSpendSkip(c.skipped) : null;
              return (
                <article
                  className={`history-card cycle-card${c.skipped ? " cycle-card--skipped" : ""}`}
                  key={c.version}
                >
                  <div className="history-card__top">
                    <strong>
                      Cycle v{c.version}
                      {c.dryRun ? " · dry-run" : ""}
                      {c.skipped ? " · skipped" : ""}
                    </strong>
                    <span>{fmtTime(c.createdAt)}</span>
                  </div>
                  {skipLabel ? (
                    <p className="cycle-skip-note">{skipLabel}</p>
                  ) : null}
                  {c.warning && !skipLabel ? (
                    <p className="cycle-skip-note">{c.warning}</p>
                  ) : null}
                  <div className="cycle-meta">
                    <span>
                      {c.skipped ? "Budget" : "Spent"}{" "}
                      <b>{fmtSol(c.cycleSol)} SOL</b>
                    </span>
                    <span>
                      Holders <b>{c.holderCount ?? "—"}</b>
                    </span>
                    {c.claim?.signature && (
                      <a
                        href={solscanTx(c.claim.signature)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Claim tx →
                      </a>
                    )}
                  </div>
                  {(c.swaps || []).length > 0 && (
                    <div className="history-syms">
                      {c.swaps.map((s) => (
                        <span key={`${c.version}-${s.symbol}`}>
                          {s.symbol}
                          {s.signature ? (
                            <>
                              {" "}
                              <a
                                href={solscanTx(s.signature)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                tx
                              </a>
                            </>
                          ) : null}
                          {s.error ? " · err" : ""}
                        </span>
                      ))}
                    </div>
                  )}
                  {(c.distributes || []).length > 0 && (
                    <div className="diff-pills">
                      {c.distributes.map((d) => (
                        <span className="pill pill--in" key={`${c.version}-d-${d.symbol}`}>
                          {d.symbol}: {d.delivered} paid
                          {d.skipped ? ` · ${d.skipped} SOL` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                  {c.indexMint && (
                    <p className="portfolio-footnote" style={{ marginTop: "0.75rem" }}>
                      Index {shortMint(c.indexMint)}
                    </p>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
